import { Divider, Grid, Paper, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Control, Controller, FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';

import Button from '@/components/Button';
import InputField from '@/components/InputField';
import useResponsive from '@/hooks/useResponsive';

const BASE_DELIVERY_TYPE_OPTIONS = [
  { value: 'SPT', label: 'Sprint' },
  { value: 'SMD', label: 'Same Day' },
];

interface StepDeliveryTypeProps<T extends FieldValues = FieldValues> {
  step: number;
  setCurrentStep: (step: number) => void;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  control: Control<T>;
  isDisabled: boolean;
  hasTLM: boolean;
}

const StepDeliveryType = <T extends FieldValues = FieldValues>({
  step,
  setCurrentStep,
  register,
  errors,
  control,
  isDisabled,
  hasTLM,
}: StepDeliveryTypeProps<T>) => {
  const deliveryTypeOptions = hasTLM
    ? [...BASE_DELIVERY_TYPE_OPTIONS, { value: 'TLM', label: 'TailorMade' }]
    : BASE_DELIVERY_TYPE_OPTIONS;
  const theme = useTheme();
  const isSmallDrawer = useResponsive('between', 'lg', 'xl');
  const isPhone = useResponsive('down', 'sm');
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          margin: '1rem 0',
          [theme.breakpoints.between('lg', 'xl')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ minWidth: '4.4rem' }}>
          Paso <span style={{ color: theme.palette.primary.main }}>3</span>
        </Typography>
        <Divider
          orientation={isSmallDrawer || isPhone ? 'horizontal' : 'vertical'}
          flexItem
          sx={{
            margin: isSmallDrawer || isPhone ? '0' : `0 ${theme.spacing(2)}`,
          }}
        />
        <Box>
          <Typography variant="subtitle2">¿A quién deseas enviar tus productos?</Typography>
        </Box>
      </Box>

      <Grid
        container
        spacing={2}
        direction="row"
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <Grid size={{ xxs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', margin: '1rem 0.5rem 0.5rem 0' }}>
            <Typography
              variant="body1"
              sx={{ color: theme.palette.secondary.main, fontSize: '1rem', fontWeight: 'bold' }}
            >
              Tipo de envío
            </Typography>
          </Box>
          <Controller
            name={'deliveryType' as Parameters<typeof control.register>[0]}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                error={!!errors?.deliveryType}
                helperText={errors?.deliveryType?.message as string | undefined}
              >
                {deliveryTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xxs: 12, md: 6 }}>
          <InputField
            label="Identificador de la orden"
            placeholder="Identificador"
            name="internalKey"
            errors={errors}
            register={register}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            //   icon={<EmailOutlinedIcon />}
          />
        </Grid>
      </Grid>
      <Grid size={{ xxs: 12, md: 6 }}>
        <InputField
          label="Notas"
          name="options.notes"
          errors={errors}
          register={register}
          sxInput={{ mt: 0 }}
          sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
          //   icon={<PersonOutlineOutlinedIcon />}
        />
      </Grid>

      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          padding: `${theme.spacing(2)} `,
          margin: `${theme.spacing(3)} 0`,
          gap: theme.spacing(2),
        }}
      >
        <Grid
          container
          spacing={2}
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Grid size={{ xxs: 12, md: 6 }}>
            <InputField
              type="number"
              label="Pago contra entrega"
              name="payments.cashOnDelivery.amount"
              errors={errors}
              register={register}
              sxInput={{ mt: 0 }}
              textFieldProps={{ defaultValue: 0 }}
              sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
              //   icon={<PhoneIphoneOutlinedIcon />}
            />
          </Grid>
          <Grid size={{ xxs: 12, md: 5.5 }}>
            <Typography variant="body2" color={theme.palette.grey[500]}>
              Si no deseas el pago contra entrega indica $0 en el valor
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="caption">
          El pago contra entrega es una forma segura de pagar el contenido de tu paquete, con la
          confianza de que quien lo recibe; lo tiene en sus manos y tu dinero será depositado por el
          equipo de 99Minutos. <br />
          <strong>El costo del servicio es del 4% del valor declarado</strong>, al hacer uso de este
          servicio aceptas &nbsp;
          <a
            style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
            href="https://www.99minutos.com/terms/"
          >
            términos y condiciones
          </a>
          .
        </Typography>
      </Paper>

      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          padding: `${theme.spacing(2)} `,
          gap: theme.spacing(2),
        }}
      >
        <Grid
          container
          spacing={2}
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Grid size={{ xxs: 12, md: 6 }}>
            <InputField
              type="number"
              label="Asegura tu envío"
              name="price.shippingPrice"
              errors={errors}
              textFieldProps={{ defaultValue: 0 }}
              register={register}
              sxInput={{ mt: 0 }}
              sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
              //   icon={<PhoneIphoneOutlinedIcon />}
            />
          </Grid>
          <Grid size={{ xxs: 12, md: 5.5 }}>
            <Typography variant="body2" color={theme.palette.grey[500]}>
              Si no deseas asegurar tu envío indica $0 en el valor
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="caption">
          Seguro por el <strong>1% del valor declarado</strong>. En caso de daño o pérdida te
          reembolsaremos el monto indicado en 3 días hábiles. Al hacer uso del servicio, aceptas los
          &nbsp;
          <a
            style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
            href="https://www.99minutos.com/terms/"
          >
            términos y condiciones.
          </a>
          <div className="strong"> Valor máximo del reembolso es $20,000 MXN.</div>
        </Typography>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xxs: 'column-reverse', sm: 'row', lg: 'column-reverse', xl: 'row' },
          justifyContent: { xxs: 'center', sm: 'space-between', lg: 'center', xl: 'space-between' },
          width: '100%',
          alignItems: 'center',
          gap: '1rem',
        }}
        mt={4}
      >
        <Button variant="outlined" onClick={() => setCurrentStep(step - 1)}>
          Atras
        </Button>
        <Button disabled={isDisabled} onClick={() => setCurrentStep(step + 1)}>
          Confirmar
        </Button>
      </Box>
    </Box>
  );
};

export default StepDeliveryType;
